/**
 * Member Invitation Modal Component (T069, US9)
 *
 * Provides UI for Standard and Enterprise tier users to invite members
 * to their branches by generating and sharing invitation codes.
 *
 * Features:
 * - Generate invitation codes
 * - Copy to clipboard functionality
 * - Show expiration (7 days)
 * - Block Free tier users with upgrade prompt
 */

import { checkFeatureAccess } from '../tier/feature-gate'
import { showUpgradePrompt } from '../tier/upgrade-modal'
import { httpsCallable } from 'firebase/functions'
import { functions } from '../core/firebase'

/**
 * Show member invitation modal
 *
 * Allows paid tier users to generate invitation codes for their branch
 */
export function showInviteModal(branchId: string, branchName: string): void {
  // Check feature access
  const { allowed, requiredTier } = checkFeatureAccess('memberInvites')

  if (!allowed && requiredTier) {
    // Free tier users blocked - show upgrade prompt
    showUpgradePrompt('memberInvites', requiredTier)
    return
  }

  // Create modal backdrop
  const backdrop = document.createElement('div')
  backdrop.className
    = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'
  backdrop.style.backdropFilter = 'blur(4px)'

  // Create modal container
  const modal = document.createElement('div')
  modal.className
    = 'bg-white rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden'

  // Create modal content
  modal.innerHTML = `
    <div class="p-6">
      <!-- Header -->
      <div class="flex items-center justify-between mb-4">
        <h2 class="text-2xl font-bold text-gray-900">
          Invite Member
        </h2>
        <button
          id="close-invite-modal"
          class="text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>

      <!-- Body -->
      <div class="mb-6">
        <p class="text-gray-600 mb-4">
          Invite members to collaborate on <strong>${branchName}</strong>
        </p>

        <!-- Email input -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-900 mb-2">
            Member Email
          </label>
          <input
            type="email"
            id="invite-email"
            placeholder="Enter member's email address"
            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
          <p class="text-xs text-gray-500 mt-1">
            The member must have an account to accept the invitation
          </p>
        </div>

        <!-- Generated code display (hidden initially) -->
        <div id="generated-code-section" class="hidden">
          <div class="bg-indigo-50 rounded-lg p-4 mb-4">
            <p class="text-sm font-medium text-gray-900 mb-2">Invitation Code</p>
            <div class="flex items-center gap-2">
              <code id="invitation-code" class="flex-1 bg-white px-3 py-2 rounded border border-indigo-200 font-mono text-sm select-all">
                <!-- Code will be inserted here -->
              </code>
              <button
                id="copy-code-btn"
                class="bg-indigo-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-indigo-700 transition-colors whitespace-nowrap"
                title="Copy to clipboard"
              >
                Copy
              </button>
            </div>
            <p class="text-xs text-gray-600 mt-2">
              ⏱️ Expires in 7 days
            </p>
          </div>

          <div class="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 space-y-2">
            <p class="text-sm text-blue-800 font-medium">
              📧 How invitees join:
            </p>
            <ol class="list-decimal list-inside text-xs text-blue-900 space-y-1">
              <li>Ask them to sign in with their account.</li>
              <li>Send them this link: <span id="accept-invite-url" class="font-mono text-[11px] break-all"></span></li>
              <li>They open the link, review the branch, and paste the invitation code if needed.</li>
            </ol>
          </div>
        </div>

        <!-- Error message -->
        <div id="invite-error" class="hidden mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p class="text-sm text-red-600"></p>
        </div>

        <!-- Success message -->
        <div id="invite-success" class="hidden mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <p class="text-sm text-green-600"></p>
        </div>

        <!-- Member limit warning -->
        <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <p class="text-xs text-yellow-800">
            ⚠️ Maximum 100 members per branch
          </p>
        </div>
      </div>

      <!-- Actions -->
      <div class="flex gap-3">
        <button
          id="generate-invite-btn"
          class="flex-1 bg-indigo-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Generate Invitation
        </button>
        <button
          id="close-invite-btn"
          class="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-200 transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  `

  backdrop.appendChild(modal)
  document.body.appendChild(backdrop)

  // Add event listeners
  const closeBtn = modal.querySelector('#close-invite-modal')
  const closeBottomBtn = modal.querySelector('#close-invite-btn')
  const generateBtn = modal.querySelector('#generate-invite-btn') as HTMLButtonElement
  const copyBtn = modal.querySelector('#copy-code-btn') as HTMLButtonElement
  const emailInput = modal.querySelector('#invite-email') as HTMLInputElement
  const errorDiv = modal.querySelector('#invite-error') as HTMLElement
  const errorText = errorDiv?.querySelector('p') as HTMLElement
  const successDiv = modal.querySelector('#invite-success') as HTMLElement
  const successText = successDiv?.querySelector('p') as HTMLElement
  const codeSection = modal.querySelector('#generated-code-section') as HTMLElement
  const codeDisplay = modal.querySelector('#invitation-code') as HTMLElement
  const acceptInviteUrlEl = modal.querySelector('#accept-invite-url') as HTMLElement

  const baseAcceptInviteUrl = `${window.location.origin.replace(/\/$/, '')}/accept-invite.html`
  if (acceptInviteUrlEl) {
    acceptInviteUrlEl.textContent = baseAcceptInviteUrl
  }

  const closeModal = () => {
    backdrop.remove()
  }

  closeBtn?.addEventListener('click', closeModal)
  closeBottomBtn?.addEventListener('click', closeModal)
  backdrop.addEventListener('click', (e) => {
    if (e.target === backdrop) {
      closeModal()
    }
  })

  // Generate invitation
  generateBtn?.addEventListener('click', async () => {
    const email = emailInput.value.trim().toLowerCase()

    if (!email) {
      errorDiv.classList.remove('hidden')
      errorText.textContent = 'Please enter an email address'
      return
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      errorDiv.classList.remove('hidden')
      errorText.textContent = 'Please enter a valid email address'
      return
    }

    errorDiv.classList.add('hidden')
    successDiv.classList.add('hidden')
    generateBtn.disabled = true
    generateBtn.textContent = 'Generating...'

    try {
      // T069: Wire up createInvitation callable
      const createInvitation = httpsCallable<
        { branchId: string, email: string },
        {
          success: boolean
          invitationId: string
          code: string
          email: string
          expiresAt: string
          branchName: string
        }
      >(functions, 'createInvitation')

      const result = await createInvitation({ branchId, email })

      if (!result.data.success) {
        throw new Error('Failed to create invitation')
      }

      console.log('Invitation generated:', result.data)

      // Show generated code
      codeDisplay.textContent = result.data.code
      if (acceptInviteUrlEl) {
        const invitationLink = `${baseAcceptInviteUrl}?code=${encodeURIComponent(result.data.code)}`
        acceptInviteUrlEl.innerHTML = `<a href="${invitationLink}" target="_blank" rel="noopener" class="text-indigo-600 hover:text-indigo-700 underline">${invitationLink}</a>`
      }
      codeSection.classList.remove('hidden')

      // Show success message
      successDiv.classList.remove('hidden')
      successText.textContent = `Invitation sent to ${email}`

      // Disable email input and generate button after successful generation
      emailInput.disabled = true
      generateBtn.textContent = 'Generated'
    } catch (error: any) {
      errorDiv.classList.remove('hidden')
      errorText.textContent = error?.message || 'Failed to generate invitation'
      generateBtn.disabled = false
      generateBtn.textContent = 'Generate Invitation'
    }
  })

  // Copy code to clipboard
  copyBtn?.addEventListener('click', async () => {
    const code = codeDisplay.textContent || ''

    try {
      await navigator.clipboard.writeText(code)

      // Show feedback
      const originalText = copyBtn.textContent
      copyBtn.textContent = 'Copied!'
      copyBtn.classList.add('bg-green-600')
      copyBtn.classList.remove('bg-indigo-600')

      setTimeout(() => {
        copyBtn.textContent = originalText
        copyBtn.classList.remove('bg-green-600')
        copyBtn.classList.add('bg-indigo-600')
      }, 2000)
    } catch (error) {
      console.error('Failed to copy code:', error)
      errorDiv.classList.remove('hidden')
      errorText.textContent = 'Failed to copy code to clipboard'
    }
  })
}
